import type { AgentTaskType } from '@septima/contract';
import {
    createContext,
    lazy,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { useFocusTrap } from '@/lib/hooks';
import { cn } from '@/lib/utils';

// The heavy drawer contents (task list, review-queue model, activity feed)
// load only once the panel is opened — closed chrome stays light (PRD #156).
const AgentPanelBody = lazy(() => import('./AgentPanelBody'));

// -------- AgentPanel open/close context --------

export interface AgentPanelFilter {
    frontend?: string;
    types?: AgentTaskType[];
}

interface AgentPanelContextValue {
    open: boolean;
    openPanel: (filter?: AgentPanelFilter) => void;
    closePanel: () => void;
    togglePanel: () => void;
}

const AgentPanelContext = createContext<AgentPanelContextValue | null>(null);

export function AgentPanelProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState<AgentPanelFilter | null>(null);

    const openPanel = useCallback((f?: AgentPanelFilter) => {
        setFilter(f ?? null);
        setOpen(true);
    }, []);
    const closePanel = useCallback(() => setOpen(false), []);
    const togglePanel = useCallback(() => setOpen((prev) => !prev), []);
    const value = useMemo<AgentPanelContextValue>(
        () => ({ open, openPanel, closePanel, togglePanel }),
        [open, openPanel, closePanel, togglePanel],
    );
    return (
        <AgentPanelContext.Provider value={value}>
            <AgentPanelFilterContext.Provider value={filter}>
                {children}
            </AgentPanelFilterContext.Provider>
        </AgentPanelContext.Provider>
    );
}

const AgentPanelFilterContext = createContext<AgentPanelFilter | null>(null);

export function useAgentPanel(): AgentPanelContextValue {
    const value = useContext(AgentPanelContext);
    if (!value) throw new Error('useAgentPanel must be used within an AgentPanelProvider');
    return value;
}

// -------- AgentPanel drawer shell --------
//
// The shell stays lightweight and always-mounted: it owns the overlay, the
// sliding container, the focus trap and the escape handler, but defers the
// heavy contents to the lazily-loaded <AgentPanelBody> which only mounts
// once the panel has been opened (PRD #156). A latch keeps the body mounted
// after the first open so re-opening is instant.

export default function AgentPanel() {
    const { open, closePanel } = useAgentPanel();
    const filter = useContext(AgentPanelFilterContext);
    const location = useLocation();
    const panelRef = useRef<HTMLDivElement>(null);
    const pathRef = useRef(location.pathname);
    const hasOpenedRef = useRef(false);
    if (open) hasOpenedRef.current = true;

    useFocusTrap(panelRef, open);

    useEffect(() => {
        if (pathRef.current === location.pathname) return;
        pathRef.current = location.pathname;
        closePanel();
    }, [location.pathname, closePanel]);

    useEffect(() => {
        if (!open) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                closePanel();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, closePanel]);

    return (
        <div
            data-testid="agent-panel"
            data-state={open ? 'open' : 'closed'}
            className={cn(
                // Full-viewport so the dim overlay shadows the sidebar too,
                // matching the run-detail / review drawers.
                'fixed inset-0 z-50',
                !open && 'pointer-events-none',
            )}
            aria-hidden={!open}
        >
            <div
                className={cn(
                    'absolute inset-0 bg-[var(--septima-scrim)]',
                    'transition-opacity duration-popover ease-swiss',
                    open ? 'opacity-100' : 'opacity-0',
                )}
                onClick={closePanel}
            />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="agent-panel-title"
                className={cn(
                    'absolute right-0 top-0 h-full w-full lg:w-[420px] xl:w-[460px]',
                    'bg-[rgb(var(--color-page))] flex flex-col',
                    'border-l border-[rgb(var(--color-divider))]',
                    'transform transition-transform duration-popover ease-swiss',
                    open ? 'translate-x-0' : 'translate-x-full',
                )}
            >
                {hasOpenedRef.current && (
                    <Suspense fallback={<PanelLoading />}>
                        <AgentPanelBody filter={filter} onClose={closePanel} />
                    </Suspense>
                )}
            </div>
        </div>
    );
}

function PanelLoading() {
    return (
        <div className="flex-1 flex items-center justify-center">
            <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                Loading agent activity…
            </span>
        </div>
    );
}
